/**
 * LoaderManager handles data loading for routes before navigation completes.
 * Implements the Loader Pattern similar to React Router 6.4+
 */

import { BehaviorSubject } from 'rxjs';

export interface LoaderContext {
    params: Record<string, string>;
    searchParams: URLSearchParams;
}

export type LoaderFunction = (context: LoaderContext) => Promise<any>;

interface CacheEntry {
    data: any;
    timestamp: number;
}

export class LoaderManager {

    public cache = new Map<string, CacheEntry>();
    private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    private cacheUpdates$ = new BehaviorSubject<void>(undefined);

    /**
     * Get observable for cache updates
     */
    public getCacheUpdates$() {
        return this.cacheUpdates$;
    }

    /**
     * Execute a loader function and cache the result
     */
    public async loadRoute(
        routePath: string,
        loader: LoaderFunction,
        params: Record<string, string>,
    ): Promise<any> {
        const cacheKey = this.getCacheKey(routePath, params);

        // Check cache first
        const cached = this.cache.get(cacheKey);
        if (cached && !this.isExpired(cached)) {
            return cached.data;
        }

        // Execute loader
        const searchParams = new URLSearchParams(window.location.search);
        const data = await loader({ params, searchParams });

        // Store in cache with BOTH keys:
        // 1. Route pattern key (for loader lookup)
        const cacheEntry = {
            data,
            timestamp: Date.now(),
        };
        this.cache.set(cacheKey, cacheEntry);

        // 2. Full pathname key (for useLoaderData lookup)
        const pathname = this.buildPathname(routePath, params);
        this.cache.set(pathname, cacheEntry);

        // Notify subscribers that cache has been updated
        this.cacheUpdates$.next(undefined);

        return data;
    }

    /**
     * Get cached data for a route
     */
    public getCachedData(routePath: string, params: Record<string, string>): any | undefined {
        const cacheKey = this.getCacheKey(routePath, params);
        const cached = this.cache.get(cacheKey);

        if (cached && !this.isExpired(cached)) {
            return cached.data;
        }

        return undefined;
    }

    /**
     * Whether the route still has a cache entry, expired or not. Absent means it
     * was never loaded or was invalidated; the TTL plays no part (#1091).
     */
    public hasEntry(routePath: string, params: Record<string, string>): boolean {
        return this.cache.has(this.getCacheKey(routePath, params));
    }

    /**
     * Invalidate cache for a specific route or all routes
     */
    public invalidate(routePath?: string) {
        if (routePath) {
            // Invalidate specific route (all param combinations)
            for (const key of this.cache.keys()) {
                if (this.matchesRoute(key, routePath)) {
                    this.cache.delete(key);
                }
            }
        } else {
            // Invalidate all
            this.cache.clear();
        }

        // Notify subscribers that cache has been invalidated
        this.cacheUpdates$.next(undefined);
    }

    /**
     * Update cached data for a route without triggering a reload.
     * Use this when you know the new values and don't need to fetch from API.
     */
    public updateCache<T>(routePath: string, updater: (data: T) => T) {
        for (const [key, entry] of this.cache.entries()) {
            if (this.matchesRoute(key, routePath)) {
                entry.data = updater(entry.data);
                entry.timestamp = Date.now(); // Refresh TTL
            }
        }
        this.cacheUpdates$.next(undefined);
    }

    /**
     * Check if a cache key matches a given route pattern
     * Handles both key formats:
     * 1. Pattern format: /manage/patient/:patientId:{"patientId":"123"}
     * 2. Pathname format: /manage/patient/123
     * Also handles relative (./) vs absolute (/) path prefixes
     */
    private matchesRoute(cacheKey: string, routePath: string): boolean {
        // Normalize paths: remove ./ prefix to handle both relative and absolute paths
        const normalizedCacheKey = cacheKey.startsWith('./') ? cacheKey.substring(1) : cacheKey;
        const normalizedRoutePath = routePath.startsWith('./') ? routePath.substring(1) : routePath;

        // Direct prefix match (pattern format)
        if (normalizedCacheKey.startsWith(normalizedRoutePath)) {
            return true;
        }

        // Convert route pattern to regex for pathname format matching
        // Example: /manage/patient/:patientId → /manage/patient/[^/]+
        const pattern = normalizedRoutePath
            .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex pattern
            .replace(/\./g, '\\.'); // Escape dots if any

        const regex = new RegExp(`^${pattern}(/|$)`);
        return regex.test(normalizedCacheKey);
    }

    private getCacheKey(routePath: string, params: Record<string, string>): string {
        return `${routePath}:${JSON.stringify(params)}`;
    }

    public isExpired(entry: CacheEntry): boolean {
        return Date.now() - entry.timestamp > this.CACHE_TTL;
    }

    /**
     * Build full pathname from route pattern and params
     * Normalizes to absolute paths (removes ./ prefix) to match window.location.pathname
     * Example: buildPathname('./view-analysis/:analysisId', { analysisId: '123' })
     *          returns '/view-analysis/123'
     */
    private buildPathname(routePath: string, params: Record<string, string>): string {
        let pathname = routePath;
        for (const [key, value] of Object.entries(params)) {
            pathname = pathname.replace(`:${key}`, value);
        }
        // Normalize to absolute path (remove ./ prefix if present)
        if (pathname.startsWith('./')) {
            pathname = pathname.substring(1); // Remove the dot, keep the slash
        }
        return pathname;
    }

}
